import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BaseTool.css';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import api from '../api';
import RatingComponent from './RatingComponent';
import useStaticRatings from './useStaticRatings';

const ParaphraseTool = () => {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTone, setSelectedTone] = useState('professional');

    // Tool ID for rating component
    const toolId = 'paraphrase';
    
    // Use static ratings for schema.org (loads immediately)
    const staticRatings = useStaticRatings(toolId);

    // Schema.org will use static ratings (available immediately)

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Free AI Paraphrasing Tool",
        "headline": "Free AI Paraphrasing Tool – Rewrite Text Online Without Plagiarism",
        "description": "Use our AI-powered paraphrasing tool to rewrite text in seconds. Choose from 15+ tones to generate plagiarism-free, natural-sounding content online.",
        "url": "https://bookwitch.io/tools/paraphrase",
        "applicationCategory": "WritingApplication",
        "operatingSystem": "Web browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "featureList": "Natural-sounding rewrites, Plagiarism-safe, Multiple tone options",
        "screenshot": "https://bookwitch.io/images/paraphrasing-tool-screenshot.jpg",
        "creator": {
            "@type": "Organization",
            "name": "Book Witch",
            "url": "https://bookwitch.io"
        },
        "potentialAction": {
            "@type": "UseAction",
            "target": "https://bookwitch.io/tools/paraphrase"
        },
        "aggregateRating": staticRatings.hasValidRatings ? {
            "@type": "AggregateRating",
            "ratingValue": staticRatings.average.toFixed(1),
            "ratingCount": staticRatings.count,
            "bestRating": "5",
            "worstRating": "1"
        } : undefined
    };

    // Remove undefined fields from jsonLd
    Object.keys(jsonLd).forEach(key => {
        if (jsonLd[key] === undefined) {
            delete jsonLd[key];
        }
    });

    // Schema is ready when static ratings are loaded (immediate or after fetch)
    const shouldRenderSchema = staticRatings.isLoaded;

    const handleProcess = async () => {
        if (!inputText.trim()) return;

        setIsLoading(true);
        try {
            const response = await api.post('/tools/paraphrase', {
                content: inputText,
                tone: selectedTone
            });
            setOutputText(response.data.modified_content);
        } catch (error) {
            console.error('Paraphrase error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const toneOptions = [
        { value: 'professional', label: 'Professional' },
        { value: 'academic', label: 'Academic' },
        { value: 'technical', label: 'Technical' },
        { value: 'scientific', label: 'Scientific' },
        { value: 'business', label: 'Business' },
        { value: 'journalistic', label: 'Journalistic' },
        { value: 'creative', label: 'Creative' },
        { value: 'storytelling', label: 'Storytelling' },
        { value: 'casual', label: 'Casual' },
        { value: 'conversational', label: 'Conversational' },
        { value: 'friendly', label: 'Friendly' },
        { value: 'persuasive', label: 'Persuasive' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'formal', label: 'Formal' },
        { value: 'informative', label: 'Informative' },
        { value: 'simplified', label: 'Simplified' },
        { value: 'engaging', label: 'Engaging' },
        { value: 'authoritative', label: 'Authoritative' }
    ];

    return (
        <>
            <Helmet>
                <title>Free AI Paraphrasing Tool – Rewrite Text Online Without Plagiarism</title>
                <meta name="description" content="Use our AI-powered paraphrasing tool to rewrite text in seconds. Choose from 15+ tones to generate plagiarism-free, natural-sounding content online." />

                <meta name="keywords" content="paraphrase tool, paraphrasing tool, text rewriter, article rewriter, sentence rephraser, free paraphrasing tool, AI paraphrasing, online paraphrasing tool, rewrite text online, plagiarism free content" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content="Rewrite Your Text with AI – Free Online Paraphrasing Tool | Book Witch" />
                <meta property="og:description" content="Instantly paraphrase any text with our AI tool. Get unique, natural-sounding rewrites with different tone options. Perfect for students, writers, and content creators." />
                <meta property="og:url" content="https://bookwitch.io/tools/paraphrase" />

                <link rel="canonical" href="https://bookwitch.io/tools/paraphrase" />
                {shouldRenderSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(jsonLd)}
                    </script>
                )}
            </Helmet>

            <Navbar />
            <div className="base-tool">
                <div className="bt-header">
                    <h1>Rewrite Your Text with AI – Free Online Paraphrasing Tool</h1>
                    <p>Whether you're a student, a content creator, or a professional writer, our AI paraphrasing tool helps you rewrite any text without losing meaning. Get a fresh version that sounds natural, unique, and plagiarism-free — instantly.</p>
                </div>

                <div className="bt-features">
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">edit_note</span>
                        <h3>Natural-sounding rewrites</h3>
                        <p>Our AI keeps your ideas intact while rewording your sentences in a human tone</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">verified</span>
                        <h3>Plagiarism-safe</h3>
                        <p>Avoid duplication issues and rewrite content that passes originality checks</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">style</span>
                        <h3>Tones for every use case</h3>
                        <p>Choose from academic, casual, professional, or creative tones and more</p>
                    </div>
                </div>

                <div className="bt-editor">
                    <textarea
                        className="textarea"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Enter your text here..."
                    />
                    <div className="bt-controls">
                        <select
                            value={selectedTone}
                            onChange={(e) => setSelectedTone(e.target.value)}
                        >
                            {toneOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={handleProcess}
                            disabled={isLoading || !inputText.trim()}
                            className="button-important"
                        >
                            <div className="inner">
                                {isLoading ? 'Processing...' : 'Paraphrase'}
                            </div>
                        </button>
                    </div>
                </div>

                {outputText && (
                    <div className="bt-output">
                        <textarea
                            className="textarea"
                            value={outputText}
                            readOnly
                        />
                        <button
                            onClick={() => navigator.clipboard.writeText(outputText)}
                            className="button-hollow"
                        >
                            <span className="material-symbols-outlined">content_copy</span>
                            Copy
                        </button>
                    </div>
                )}

                <RatingComponent toolId={toolId} />

                <section className="bt-section">
                    <div className="bt-section-header">
                        <h2>How it works</h2>
                    </div>

                    <div className="bt-use-cases-grid">
                        <div className="bt-use-case-card">
                            <h3>1. Paste your text</h3>
                            <p>Paste your text into the field above.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>2. Pick a tone</h3>
                            <p>Pick a tone or style that fits your need.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>3. Get results</h3>
                            <p>Click <strong>Paraphrase</strong> and get a fresh version instantly.</p>
                        </div>
                    </div>
                </section>

                <section className="bt-cta">
                    <h2>Looking to go further?</h2>
                    <p>You can use your paraphrased content as a foundation to write ebooks, guides, or articles with Book Witch — our AI writing platform.</p>
                    <p>Turn small texts into big ideas. Book Witch helps you scale your writing.</p>
                    <div className="bt-cta-buttons">
                        <Link to="/app" className="no-style-link">
                            <button className="button-important">
                                <div className="inner">
                                    Try Book Witch
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </div>
                            </button>
                        </Link>
                    </div>
                </section>

                <section className="bt-faq">
                    <h2>FAQ – AI Paraphrasing Tool</h2>
                    <div className="bt-faq-item">
                        <h3>Is this paraphrasing tool free to use?</h3>
                        <p>Yes, our basic paraphrasing tool is completely free to use with no hidden costs or limitations.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Can I use the paraphrased content for academic papers?</h3>
                        <p>While our tool creates unique content, we recommend always citing your sources properly according to your institution's guidelines. The tool is meant to help rephrase ideas, not to avoid proper citation.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>How does the AI understand which parts to keep intact?</h3>
                        <p>Our advanced AI has been trained to understand context and meaning, allowing it to preserve key concepts and information while changing the wording and structure.</p>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default ParaphraseTool; 